import * as XLSX from 'xlsx';
import type { ReportExportContext } from './context';
import { sanitizeSheetName } from './sheetName';
import type { CustomerPurchaseDetailResponse, InventoryTrackingEvent } from '../../../services/report.service';

type Row = Record<string, string | number>;

interface BuildOpts {
  inventoryTracking?: Map<string, InventoryTrackingEvent[]>;
  customerDetails?: Map<string, CustomerPurchaseDetailResponse>;
}

const trackingRows = (events: InventoryTrackingEvent[], t: ReportExportContext['t']): Row[] =>
  events.map((e) => ({
    [t('reports.tracking.table.date')]: e.date,
    [t('reports.tracking.table.direction')]: e.direction === 'in' ? t('reports.tracking.directionIn') : t('reports.tracking.directionOut'),
    [t('reports.tracking.table.reference')]: e.referenceLabel,
    [t('reports.tracking.table.counterparty')]: e.counterparty,
    [t('reports.tracking.table.lot')]: e.lotNumber || '',
    [t('reports.tracking.table.quantity')]: e.quantity,
  }));

const customerSheetAoa = (
  detail: CustomerPurchaseDetailResponse,
  t: ReportExportContext['t']
): (string | number)[][] => {
  const rows: (string | number)[][] = [];
  rows.push([t('reports.customer.summary.totalPcs'), detail.summary.totalPcs]);
  rows.push([t('reports.customer.summary.totalOrders'), detail.summary.totalOrders]);
  rows.push([t('reports.customer.summary.totalRevenue'), detail.summary.totalRevenue]);
  rows.push([
    t('reports.customer.summary.topFinishedGood'),
    detail.summary.topFinishedGood ? `${detail.summary.topFinishedGood.name} (${detail.summary.topFinishedGood.pcs})` : '-',
  ]);
  rows.push([]);
  rows.push([
    t('reports.export.columns.finishedGood'),
    t('reports.customer.history.date'),
    t('reports.customer.history.invoice'),
    t('reports.customer.history.qty'),
  ]);
  const fgs = [...detail.finishedGoods].sort((a, b) => b.totalPcs - a.totalPcs || a.name.localeCompare(b.name));
  let grand = 0;
  for (const fg of fgs) {
    for (const p of fg.purchases) rows.push([fg.name, p.date, p.orderNumber, p.qty]);
    rows.push(['', '', t('reports.export.subtotal'), fg.totalPcs]);
    grand += fg.totalPcs;
  }
  rows.push(['', '', t('reports.export.grandTotal'), grand]);
  return rows;
};

export function buildReportWorkbook(ctx: ReportExportContext, opts: BuildOpts = {}): XLSX.WorkBook {
  const { mode, t } = ctx;
  const wb = XLSX.utils.book_new();
  const used = new Set<string>();
  const append = (ws: XLSX.WorkSheet, name: string) => XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(name, used));

  if (mode === 'roasting') {
    const data: Row[] = ctx.roastingReport.rows.map((row) => ({
      [t('reports.export.columns.roastingDate')]: row.roastingDate,
      [t('reports.export.columns.batchNumber')]: row.batchNumber,
      [t('reports.export.columns.greenBeanCode')]: row.greenBeanCode,
      [t('reports.export.columns.greenBeanName')]: row.greenBeanName,
      [t('reports.export.columns.inputWeight')]: row.inputWeight,
      [t('reports.export.columns.outputWeight')]: row.outputWeight,
      [t('reports.export.columns.weightLossPercent')]: row.weightLossPercent,
    }));
    append(XLSX.utils.json_to_sheet(data), t('reports.export.sheetName'));
  } else if (mode === 'greenBeans' || mode === 'roastedBeans' || mode === 'finishedGoods') {
    const reportData: Row[] =
      mode === 'greenBeans'
        ? ctx.greenBeansReport.rows.map((row) => ({
            [t('reports.export.columns.itemCode')]: row.itemCode,
            [t('reports.export.columns.itemName')]: row.itemName,
            [t('reports.export.columns.supplier')]: row.supplierName,
            [t('reports.export.columns.remainingQuantity')]: row.remainingQuantity,
            [t('reports.export.columns.unit')]: row.unitOfMeasure,
            [t('reports.export.columns.averageCost')]: row.averageCost,
          }))
        : mode === 'roastedBeans'
          ? ctx.roastedBeansReport.rows.map((row) => ({
              [t('reports.export.columns.itemCode')]: row.itemCode,
              [t('reports.export.columns.itemName')]: row.itemName,
              [t('reports.export.columns.relatedBatch')]: row.relatedBatch,
              [t('reports.export.columns.remainingQuantity')]: row.remainingQuantity,
              [t('reports.export.columns.unit')]: row.unitOfMeasure,
              [t('reports.export.columns.averageCost')]: row.averageCost,
            }))
          : ctx.finishedGoodsReport.rows.map((row) => ({
              [t('reports.export.columns.itemCode')]: row.itemCode,
              [t('reports.export.columns.itemName')]: row.itemName,
              [t('reports.export.columns.remainingQuantity')]: row.remainingQuantity,
              [t('reports.export.columns.unit')]: row.unitOfMeasure,
              [t('reports.export.columns.averageCost')]: row.averageCost,
              [t('reports.export.columns.lastProduction')]: row.lastProductionDate || '',
            }));
    append(XLSX.utils.json_to_sheet(reportData), t('reports.export.sheetName'));

    const itemRows =
      mode === 'greenBeans' ? ctx.greenBeansReport.rows
        : mode === 'roastedBeans' ? ctx.roastedBeansReport.rows
          : ctx.finishedGoodsReport.rows;
    if (opts.inventoryTracking) {
      for (const row of itemRows) {
        const events = opts.inventoryTracking.get(row.itemId);
        if (events && events.length > 0) {
          append(XLSX.utils.json_to_sheet(trackingRows(events, t)), row.itemCode || row.itemId);
        }
      }
    }
  } else if (mode === 'business') {
    const custData: Row[] = ctx.businessReport.customerBreakdown.map((row) => ({
      [t('reports.export.columns.customerName')]: row.customerName,
      [t('reports.export.columns.orderCount')]: row.orderCount,
      [t('reports.export.columns.totalRevenue')]: row.totalRevenue,
      [t('reports.export.columns.lastOrderDate')]: row.lastOrderDate || '',
    }));
    append(XLSX.utils.json_to_sheet(custData), t('reports.export.sheetCustomers'));
    const histData: Row[] = ctx.businessReport.purchaseHistory.map((row) => ({
      [t('reports.export.columns.orderNumber')]: row.orderNumber,
      [t('reports.export.columns.orderDate')]: row.orderDate,
      [t('reports.export.columns.customerName')]: row.customerName,
      [t('reports.export.columns.totalAmount')]: row.totalAmount,
      [t('reports.export.columns.status')]: row.status,
    }));
    append(XLSX.utils.json_to_sheet(histData), t('reports.export.sheetSalesHistory'));
  } else {
    // customer
    const listData: Row[] = ctx.customerReport.customers.map((row) => ({
      [t('reports.export.columns.customerName')]: row.customerName,
      [t('reports.export.columns.customerCode')]: row.customerCode,
      [t('reports.export.columns.orderCount')]: row.orderCount,
      [t('reports.export.columns.totalPcs')]: row.totalPcs,
      [t('reports.export.columns.totalRevenue')]: row.totalRevenue,
      [t('reports.export.columns.lastOrderDate')]: row.lastOrderDate || '',
    }));
    append(XLSX.utils.json_to_sheet(listData), t('reports.export.sheetCustomers'));
    if (opts.customerDetails) {
      for (const c of ctx.customerReport.customers) {
        const detail = opts.customerDetails.get(c.customerId);
        if (detail) {
          append(XLSX.utils.aoa_to_sheet(customerSheetAoa(detail, t)), c.customerCode || c.customerName || c.customerId);
        }
      }
    }
  }

  return wb;
}
